import { useState, type ReactNode } from 'react';
import { ConfirmDialog, EmptyState } from '@/shared/components';
import { toApiError, type ArrivalStepResponse } from '@/shared/api';
import {
  emptyArrivalDraft,
  toArrivalDraft,
  toUpsertRequest,
  type ArrivalStepDraft,
} from './arrivalDraft';
import { ArrivalGuidePreview } from './ArrivalGuidePreview';
import { arrivalProblem, staleAfter } from './arrivalProblem';
import { ArrivalStepList } from './ArrivalStepList';
import { ArrivalStepSheet } from './ArrivalStepSheet';
import { GUIDE_COPY } from './guideCopy';
import { useArrivalGuideApi, type ArrivalScope } from './useArrivalGuideApi';

interface Props {
  scope: ArrivalScope;
  canEdit: boolean;
  /** Steht in der Handy-Vorschau vor den Schritten, etwa die eigenen Stellplätze. */
  previewLead?: (locale: 'de' | 'en') => ReactNode;
}

/**
 * Anfahrt oder Parkmöglichkeiten, am Objekt wie am Apartment: Karten in der Reihenfolge des Wegs,
 * Bearbeiten im Sheet und daneben, was der Gast sieht.
 */
export function ArrivalGuideEditor({ scope, canEdit, previewLead }: Props) {
  const { query, save, remove, move, resetOverride, refresh } = useArrivalGuideApi(scope);
  const guide = scope.guide ?? 'Arrival';
  const copy = GUIDE_COPY[guide];
  const [editing, setEditing] = useState<{ stepId?: string; draft: ArrivalStepDraft } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ArrivalStepResponse | null>(null);
  const [openImages, setOpenImages] = useState<string | null>(null);

  // Nur ohne jeden Stand ein Fehlerschirm: scheitert ein späteres Nachladen, bleiben Liste und
  // ein offenes Sheet mit seinem Entwurf stehen.
  if (!query.data) {
    return query.isError ? (
      <EmptyState
        icon="Error"
        title={copy.loadFailed}
        description="Bitte versuchen Sie es erneut."
        action={{ label: 'Erneut laden', onClick: () => void query.refetch() }}
      />
    ) : (
      <p className="text-ink-soft text-sm">Lade {copy.name}…</p>
    );
  }

  const { steps, placeholders } = query.data;
  const open = (next: { stepId?: string; draft: ArrivalStepDraft }) => {
    setSaveError(null);
    setEditing(next);
  };

  return (
    <div className="@container space-y-3">
      {query.isError && (
        <p role="status" className="text-sealwax text-sm">
          Neu laden fehlgeschlagen – zu sehen ist der zuletzt geladene Stand.
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="text-sage ml-2 min-h-11 font-semibold underline underline-offset-2"
          >
            Erneut laden
          </button>
        </p>
      )}
      <div className="@4xl:grid-cols-[minmax(0,1fr)_20rem] grid items-start gap-6">
        <ArrivalStepList
          scope={scope.kind}
          steps={steps}
          copy={copy}
          canEdit={canEdit}
          busy={save.isPending || remove.isPending || move.isPending || resetOverride.isPending}
          openImages={openImages}
          onAdd={() => open({ draft: emptyArrivalDraft() })}
          onEdit={(step) => open({ stepId: step.id, draft: toArrivalDraft(step) })}
          onMove={(step, direction) => move.mutate({ stepId: step.id, direction })}
          onDelete={setPendingDelete}
          onResetOverride={(step) => resetOverride.mutate(step.id)}
          onToggleImages={(stepId) => setOpenImages(openImages === stepId ? null : stepId)}
        />
        <ArrivalGuidePreview scope={scope} guide={guide} steps={steps} lead={previewLead} />
      </div>

      {editing && (
        <ArrivalStepSheet
          scope={scope}
          stepId={editing.stepId}
          initial={editing.draft}
          placeholders={placeholders}
          saving={save.isPending}
          error={saveError}
          onClose={() => setEditing(null)}
          onSave={(draft) =>
            save.mutate(
              { stepId: editing.stepId, body: toUpsertRequest(draft) },
              {
                onSuccess: () => setEditing(null),
                onError: async (error) => {
                  const problem = await toApiError(error);
                  setSaveError(arrivalProblem(problem));
                  // Womöglich ist der Schritt doch angekommen: ein zweites Anlegen darf ihn nicht
                  // verdoppeln, also den Stand darunter neu holen.
                  if (staleAfter(problem)) void refresh();
                },
              },
            )
          }
        />
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(next) => !next && setPendingDelete(null)}
        title={copy.deleteTitle}
        description={
          scope.kind === 'object'
            ? 'Der Eintrag verschwindet in allen Apartments dieses Objekts, Überschreibungen einzelner Apartments mit ihm.'
            : 'Der Eintrag verschwindet in diesem Apartment.'
        }
        confirmLabel="Löschen"
        loading={remove.isPending}
        onConfirm={() => {
          if (pendingDelete) {
            remove.mutate(pendingDelete.id, { onSettled: () => setPendingDelete(null) });
          }
        }}
      />
    </div>
  );
}
